import { useState } from "react";
import { useT, useLanguage, translateError } from "../i18n/hooks.ts";
import { parseDailySeriesAsync } from "../model/csvWorkerClient.ts";
import { resolveIndicesByName, type DailySeries } from "../model/dailySeries.ts";
import { detectScale, type SeriesScale } from "../model/seriesScale.ts";
import { FileButton } from "./FileButton.tsx";
import { InfoIcon } from "./InfoIcon.tsx";

/** Nº of entities that already have their own column in this series, from the already-persisted index (0 = unlinked) — see resolveIndicesByName (dailySeries.ts). */
function matches<T>(
  series: DailySeries,
  entities: T[],
  columnIndexOf: (entity: T) => number
): number {
  return entities.filter((e) => {
    const i = columnIndexOf(e);
    return i > 0 && i - 1 < series.columns.length;
  }).length;
}

/**
 * One series' upload field (precipitation/ETP/temperature/pumping) —
 * generic over the entity it links against (sub-basins or aquifers).
 * Used both by the new-project wizard (Step5Series.tsx, one instance per
 * step) and the editor's series tab (SeriesPanel.tsx) — lives directly
 * under ui/ (not wizard/) so neither has to import it from the other, see
 * project_evalhid_code_audit.md.
 */
export function SeriesLoader<T, U = never>({
  label,
  required,
  entities,
  entityPlural,
  nameOf,
  columnIndexOf,
  withIndex,
  series,
  onLoaded,
  onEntitiesUpdated,
  secondaryGroup,
  showLabel = true,
  formatHelp,
  onLoadingChange,
  scale,
  scaleLocked,
}: {
  label: string;
  required: boolean;
  entities: T[];
  /** For the "X of Y ... with their own column" message — "sub-basins"/"aquifers". */
  entityPlural: string;
  nameOf: (entity: T) => string;
  columnIndexOf: (entity: T) => number;
  withIndex: (entity: T, index: number) => T;
  series: DailySeries | null;
  onLoaded: (s: DailySeries) => void;
  /**
   * Entities with the index already resolved by name against the newly
   * uploaded series — persist via onChangeProject. When `secondaryGroup` is
   * given, its own freshly-resolved entities arrive together as the second
   * argument, and any inferred scale (see `scaleLocked` below) as the
   * third, in this SAME call — never as separate calls of their own: two
   * independent `onChangeProject`-shaped calls built from the same stale
   * `project` closure would race (whichever ran second would silently
   * discard the other's change, since neither reads back the just-committed
   * state — every caller in this app writes the whole project object, not a
   * reducer updater).
   */
  onEntitiesUpdated: (entities: T[], secondaryEntities?: U[], inferredScale?: SeriesScale) => void;
  /**
   * A second, independent entity group resolved by name against the SAME
   * uploaded file — pumping is the one series where this applies:
   * Ent_Bomb.csv links both sub-basins AND aquifers by name in one shared
   * file, two fully independent resolutions (see SubBasin.pumpingCol's own
   * doc comment), not one derived from the other. Every other series
   * (precipitation/ETP/temperature) only ever links sub-basins, so this is
   * omitted there.
   */
  secondaryGroup?: {
    entities: U[];
    entityPlural: string;
    nameOf: (entity: U) => string;
    columnIndexOf: (entity: U) => number;
    withIndex: (entity: U, index: number) => U;
  };
  /** Hides the built-in label — for callers that already show the same title elsewhere (SeriesPanel.tsx's own <h4>), so it doesn't appear twice. */
  showLabel?: boolean;
  /** Format-help text (CSV format, which name the column header must match) — entity-specific (sub-basin/aquifer), shown as an info icon next to the file button instead of a shared paragraph. Omitted in the creation wizard, where each SeriesLoader is its own step and doesn't need this repeated per field. */
  formatHelp?: string;
  /** Reports whether a file is currently being parsed — lets the caller (SeriesPanel.tsx) show a spinner over its chart instead of leaving it stale/blank during the (real-project-sized, so not instant) parse. */
  onLoadingChange?: (loading: boolean) => void;
  /** The project's own scale (EvalHidProject.seriesScale) — decides what date format the uploaded CSV's rows are expected to be in once `scaleLocked` (see parseDailySeries, dailySeries.ts). */
  scale: SeriesScale;
  /**
   * Whether the project's scale is already committed
   * (isSeriesScaleLocked, dailySeries.ts). While `false`, the CSV's dates
   * are read tolerantly (either shape) and the real scale is inferred from
   * their own spacing (`detectScale`) instead of being enforced against
   * `scale` — same bootstrap parsing already used when opening a project
   * from a folder for the first time (project.ts/importCsvProject.ts).
   */
  scaleLocked: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const language = useLanguage();

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    onLoadingChange?.(true);
    try {
      const newSeries = await parseDailySeriesAsync(
        await file.arrayBuffer(),
        language,
        scaleLocked ? scale : undefined
      );
      // Same >=2-dates requirement as PasteDailySeries.tsx's own apply() —
      // a single date's own shape can't tell daily from monthly apart.
      const inferredScale =
        !scaleLocked && newSeries.dates.length >= 2 ? detectScale(newSeries.dates) : undefined;
      onLoaded(newSeries);
      const resolvedPrimary = resolveIndicesByName(entities, newSeries, nameOf, withIndex);
      const resolvedSecondary = secondaryGroup
        ? resolveIndicesByName(
            secondaryGroup.entities,
            newSeries,
            secondaryGroup.nameOf,
            secondaryGroup.withIndex
          )
        : undefined;
      onEntitiesUpdated(resolvedPrimary, resolvedSecondary, inferredScale);
    } catch (err) {
      // Real ambiguity found live: `onLoaded`/`onEntitiesUpdated` above only
      // ever run once parsing/resolving has fully succeeded — a thrown
      // error here (e.g. a malformed date) always leaves the ALREADY-OPEN
      // project's own series completely untouched. The raw translated
      // error alone (just the bad date/value) didn't say that explicitly,
      // which reads ambiguously for a big multi-thousand-row file: did the
      // first N good rows apply before it hit the bad one? They didn't —
      // this makes that explicit instead of leaving it to be inferred.
      setError(t("seriesLoader.importFailed", { error: translateError(err, t) }));
    } finally {
      onLoadingChange?.(false);
    }
  }

  return (
    <div className="series-step">
      {showLabel && (
        <label>
          {label} {required ? "" : t("common.optional")}
        </label>
      )}
      {series && (
        <p>
          {t("seriesLoader.summary", {
            rows: series.dates.length,
            m: matches(series, entities, columnIndexOf),
            total: entities.length,
            plural: entityPlural,
          })}
        </p>
      )}
      {series && secondaryGroup && (
        <p>
          {t("seriesLoader.summary", {
            rows: series.dates.length,
            m: matches(series, secondaryGroup.entities, secondaryGroup.columnIndexOf),
            total: secondaryGroup.entities.length,
            plural: secondaryGroup.entityPlural,
          })}
        </p>
      )}
      <div className="series-step-upload">
        <FileButton accept=".csv" onFiles={(files) => onFile(files?.[0])} />
        {formatHelp && <InfoIcon text={formatHelp} />}
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
